import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Print as PrintIcon,
  Clear as ClearIcon,
  Description as DescriptionIcon,
  Delete as DeleteIcon,
  Lock as LockIcon,
  LockOutlined as LockOutlinedIcon,
  Comment as CommentIcon,
} from '@mui/icons-material';
import { useProductosMaterialContext } from 'context';
import { tienePermisosModulo } from 'lib/seguridad/permisosPorModulo';
import { FP, PageHeader, fpInputSx } from 'components/financepro';
import VistaCotizacion from './VistaCotizacion';

export const PROGRAMA_LISTADO_COTIZACIONES = 'listadocotizaciones';
export const PROGRAMA_ELIMINAR_COTIZACIONES = 'eliminarcotizaciones';

const cardShellSx = {
  borderRadius: 2.5,
  border: `1px solid ${FP.color.border}`,
  bgcolor: FP.color.cardBg,
  boxShadow: FP.shadow.soft,
  overflow: 'hidden',
};

const cellHeadSx = {
  fontWeight: 700,
  fontSize: '0.75rem',
  color: FP.color.textSecondary,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
};

const ListadoCotizaciones = ({ autoCargar = true, showPageHeader = true }) => {
  const { controller, selectedCompany, showSuccess, showError } = useProductosMaterialContext();
  const { nombreNegocio, usuarioLog, claveLog, roleDetalles } = controller;
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const puedeLeer = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_LISTADO_COTIZACIONES),
    [roleDetalles]
  );
  const puedeEliminar = useMemo(
    () => tienePermisosModulo(roleDetalles, PROGRAMA_ELIMINAR_COTIZACIONES),
    [roleDetalles]
  );

  const [cotizaciones, setCotizaciones] = useState([]);
  const [cargando, setCargando] = useState(false);

  const [nombreCliente, setNombreCliente] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');

  const [vistaCotizacionAbierta, setVistaCotizacionAbierta] = useState(false);
  const [datosVistaCotizacion, setDatosVistaCotizacion] = useState({});
  const [cargandoReimprimir, setCargandoReimprimir] = useState(null);
  const [cargandoCerrar, setCargandoCerrar] = useState(null);
  const [comentarioDialogOpen, setComentarioDialogOpen] = useState(false);
  const [cotizacionComentario, setCotizacionComentario] = useState(null);
  const [comentarioTexto, setComentarioTexto] = useState('');
  const [fechaUltimaConversacion, setFechaUltimaConversacion] = useState('');
  const [cargandoComentario, setCargandoComentario] = useState(false);

  const cargarCotizaciones = useCallback(async () => {
    if (!puedeLeer) {
      showError('No tiene permisos para ver el listado de cotizaciones');
      return;
    }
    if (!usuarioLog) return;

    setCargando(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_listarcotizaciones', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          nombre_cliente: nombreCliente,
          fecha_desde: fechaDesde,
          fecha_hasta: fechaHasta,
          estado: estadoFiltro,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const lista = data.cotizaciones || [];
        setCotizaciones(lista);
        if (lista.length === 0) {
          showSuccess('No se encontraron cotizaciones con los filtros aplicados');
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar cotizaciones');
      }
    } catch {
      showError('Error de conexión al cargar cotizaciones');
    } finally {
      setCargando(false);
    }
  }, [
    puedeLeer,
    selectedCompany,
    usuarioLog,
    claveLog,
    servidor,
    nombreCliente,
    fechaDesde,
    fechaHasta,
    estadoFiltro,
    showError,
    showSuccess,
  ]);

  useEffect(() => {
    if (puedeLeer && autoCargar) {
      cargarCotizaciones();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleLimpiarFiltros = () => {
    setNombreCliente('');
    setFechaDesde('');
    setFechaHasta('');
    setEstadoFiltro('');
  };

  const handleImprimir = () => {
    window.print();
  };

  const handleReimprimir = async (cotizacion) => {
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    setCargandoReimprimir(cotizacion.id);
    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenercotizacion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          cotizacion_id: cotizacion.id,
        }),
      });
      if (response.ok) {
        const data = await response.json();
        const c = data.cotizacion || {};
        const items = (data.items || []).map((it, idx) => ({
          id: it.id || idx,
          codigo: it.codigo,
          descripcion: it.descripcion,
          cantidad: parseFloat(it.cantidad) || 0,
          precio: parseFloat(it.precio) || 0,
          descuento: parseFloat(it.descuento) || 0,
          impuesto: parseFloat(it.impuesto) || 0,
          subtotal: parseFloat(it.subtotal) || 0,
        }));
        const subtotalAntesDescuentos = items.reduce((s, i) => s + (i.cantidad * i.precio), 0);
        const totalDescuentos = items.reduce((s, i) => s + (i.descuento || 0), 0);
        setDatosVistaCotizacion({
          numero: c.numero,
          cliente: c.cliente,
          direccion: c.direccion || '',
          direccion_envio: c.direccion_envio || '',
          telefono: c.telefono || '',
          items,
          subtotalAntesDescuentos,
          totalDescuentos,
          totalImpuestos: parseFloat(c.impuestos) || 0,
          subtotal: parseFloat(c.subtotal) || 0,
          total: parseFloat(c.total) || 0,
          fecha: c.fecha || '',
          observaciones: c.observaciones || '',
          id_empresa: selectedCompany,
          empresa: selectedCompany?.empresa || selectedCompany?.nombre || '',
        });
        setVistaCotizacionAbierta(true);
      } else {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'Error al cargar la cotización');
      }
    } catch {
      showError('Error de conexión');
    } finally {
      setCargandoReimprimir(null);
    }
  };

  const handleCerrar = async (cotizacion) => {
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (!window.confirm(`¿Cerrar la cotización Nº ${cotizacion.numero}? Una vez cerrada no podrá editarse.`)) return;
    setCargandoCerrar(cotizacion.id);
    try {
      const response = await fetch(servidor + '/api/facturabackend_cerrarcotizacion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          cotizacion_id: cotizacion.id,
        }),
      });
      if (response.ok) {
        showSuccess('Cotización cerrada');
        cargarCotizaciones();
      } else {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'Error al cerrar cotización');
      }
    } catch {
      showError('Error de conexión');
    } finally {
      setCargandoCerrar(null);
    }
  };

  const handleAbrirComentario = (cotizacion) => {
    setCotizacionComentario(cotizacion);
    setComentarioTexto(cotizacion.comentario_estado_cliente || '');
    setFechaUltimaConversacion(new Date().toISOString().slice(0, 10));
    setComentarioDialogOpen(true);
  };

  const handleCerrarComentarioDialog = () => {
    setComentarioDialogOpen(false);
    setCotizacionComentario(null);
    setComentarioTexto('');
    setFechaUltimaConversacion('');
  };

  const requiereRevisionMensaje = (cotizacion) => {
    const f = cotizacion?.fecha_ultima_conversacion;
    if (!f) return false;
    const hoy = new Date();
    hoy.setHours(0, 0, 0, 0);
    const fechaConv = new Date(f);
    fechaConv.setHours(0, 0, 0, 0);
    const diffDias = Math.floor((hoy - fechaConv) / (1000 * 60 * 60 * 24));
    return diffDias > 5;
  };

  const handleGuardarComentario = async () => {
    if (!cotizacionComentario) return;
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    setCargandoComentario(true);
    try {
      const response = await fetch(servidor + '/api/facturabackend_actualizarcomentariocotizacion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          cotizacion_id: cotizacionComentario.id,
          comentario_estado_cliente: comentarioTexto,
        }),
      });
      if (response.ok) {
        showSuccess('Comentario guardado');
        handleCerrarComentarioDialog();
        cargarCotizaciones();
      } else {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'Error al guardar comentario');
      }
    } catch {
      showError('Error de conexión');
    } finally {
      setCargandoComentario(false);
    }
  };

  const handleEliminar = async (cotizacion) => {
    if (!puedeEliminar) {
      showError('No tiene permiso para eliminar cotizaciones.');
      return;
    }
    if (!usuarioLog) {
      showError('Sesión inválida: falta usuario de login');
      return;
    }
    if (cotizacion.estado === 'IMPORTADA') {
      showError('No se puede eliminar una cotización ya importada en una factura.');
      return;
    }
    if (!window.confirm(`¿Eliminar la cotización Nº ${cotizacion.numero}?`)) return;
    try {
      const response = await fetch(servidor + '/api/facturabackend_eliminarcotizacion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: selectedCompany,
          usuario: usuarioLog || '',
          password: claveLog || '',
          cotizacion_id: cotizacion.id,
        }),
      });
      if (response.ok) {
        showSuccess('Cotización eliminada');
        cargarCotizaciones();
      } else {
        const err = await response.json().catch(() => ({}));
        showError(err.Error || 'Error al eliminar');
      }
    } catch {
      showError('Error de conexión');
    }
  };

  const formatearFecha = (fecha) => {
    if (!fecha) return '—';
    try {
      return new Date(fecha).toLocaleDateString('es-DO', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
    } catch {
      return fecha;
    }
  };

  const sumaSubtotal = cotizaciones.reduce((s, c) => s + (parseFloat(c.subtotal) || 0), 0);
  const sumaTotal = cotizaciones.reduce((s, c) => s + (parseFloat(c.total) || 0), 0);

  if (!puedeLeer) {
    return (
      <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
        {showPageHeader && (
          <PageHeader
            title="Listado de cotizaciones"
            subtitle={nombreNegocio ? `${nombreNegocio} · Seguimiento` : 'Seguimiento'}
          />
        )}
        <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, display: 'flex', justifyContent: 'center' }}>
          <Card
            elevation={0}
            sx={{ ...cardShellSx, maxWidth: 560, width: '100%', textAlign: 'center', p: { xs: 3, md: 4 } }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: 1.6,
                mx: 'auto',
                mb: 2.5,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: FP.color.dangerSoft,
                color: FP.color.danger,
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: '1.25rem',
                color: FP.color.textPrimary,
                mb: 1,
                letterSpacing: '-0.01em',
              }}
            >
              Acceso denegado
            </Typography>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
              No tiene permiso del programa «{PROGRAMA_LISTADO_COTIZACIONES}» en roles para ver el listado.
            </Typography>
            <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
              Por favor, contacte al administrador del sistema para solicitar los permisos necesarios.
            </Typography>
          </Card>
        </Box>
      </Box>
    );
  }

  const filterHeaderSx = {
    px: { xs: 2, md: 3 },
    py: 1.6,
    borderBottom: `1px solid ${FP.color.border}`,
    bgcolor: '#FAFBFE',
    display: 'flex',
    alignItems: 'center',
    gap: 1.2,
  };

  return (
    <Box component="main" sx={{ minHeight: '100vh', bgcolor: FP.color.surface }}>
      {showPageHeader && (
        <PageHeader
          title="Listado de cotizaciones"
          subtitle={nombreNegocio ? `${nombreNegocio} · Seguimiento` : 'Seguimiento'}
        />
      )}

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }} id="printable-section">
        <Card elevation={0} sx={{ ...cardShellSx, mb: 3 }}>
          <Box sx={filterHeaderSx}>
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de búsqueda
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Button
              variant="contained"
              startIcon={<PrintIcon />}
              onClick={handleImprimir}
              className="no-print"
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 1.4,
                bgcolor: FP.color.navy,
                color: '#FFFFFF',
                boxShadow: 'none',
                '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              }}
            >
              Imprimir
            </Button>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }} className="no-print">
            <Grid container spacing={2.5} alignItems="flex-end">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Nombre del cliente"
                  value={nombreCliente}
                  onChange={(e) => setNombreCliente(e.target.value)}
                  placeholder="Buscar por nombre del cliente"
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <FormControl fullWidth sx={fpInputSx}>
                  <InputLabel id="filtro-estado-label" shrink>
                    Estado
                  </InputLabel>
                  <Select
                    labelId="filtro-estado-label"
                    id="filtro-estado"
                    value={estadoFiltro}
                    label="Estado"
                    onChange={(e) => setEstadoFiltro(e.target.value)}
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="PENDIENTE">Pendiente</MenuItem>
                    <MenuItem value="CERRADA">Cerrada</MenuItem>
                    <MenuItem value="IMPORTADA">Importada en factura</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={fpInputSx}
                />
              </Grid>
              <Grid item xs={12} sm={12} md={3}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.25 }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={cargarCotizaciones}
                    disabled={cargando || !usuarioLog}
                    sx={{
                      height: 46,
                      bgcolor: FP.color.navy,
                      color: '#FFFFFF',
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      boxShadow: 'none',
                      flex: 1,
                      '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                      '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
                    }}
                  >
                    Buscar
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleLimpiarFiltros}
                    sx={{
                      height: 46,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: '#FFFFFF',
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontWeight: 700,
                      flex: 1,
                      '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                    }}
                  >
                    Limpiar
                  </Button>
                </Box>
              </Grid>
            </Grid>
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.86rem', mt: 2 }}>
              Total de cotizaciones encontradas: <strong>{cotizaciones.length}</strong>
            </Typography>
          </CardContent>
        </Card>

        <Card elevation={0} sx={cardShellSx}>
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Detalle de cotizaciones
            </Typography>
            {cargando && (
              <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', fontWeight: 600 }}>
                Cargando…
              </Typography>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {cargando ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress size={28} sx={{ color: FP.color.accentBlue }} />
              </Box>
            ) : cotizaciones.length === 0 ? (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Use los filtros y pulse «Buscar» para listar las cotizaciones.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ border: `1px solid ${FP.color.border}`, borderRadius: 2, overflow: 'auto' }}
              >
                <Table sx={{ tableLayout: 'fixed', width: '100%', minWidth: { xs: 1100, sm: 1200 } }}>
                  <colgroup>
                    {['9%', '11%', '14%', '9%', '10%', '10%', '10%', '11%', '16%'].map((w, i) => (
                      <col key={i} style={{ width: w }} />
                    ))}
                  </colgroup>
                  <TableBody>
                    <TableRow
                      sx={{
                        position: 'sticky',
                        top: 0,
                        zIndex: 2,
                        '& td': {
                          border: 'none',
                          borderBottom: `2px solid ${FP.color.border}`,
                          bgcolor: FP.color.surfaceAlt,
                        },
                      }}
                    >
                      <TableCell sx={cellHeadSx}>Número</TableCell>
                      <TableCell sx={cellHeadSx}>Nombre</TableCell>
                      <TableCell sx={cellHeadSx}>Cliente</TableCell>
                      <TableCell align="center" sx={cellHeadSx}>Fecha</TableCell>
                      <TableCell align="right" sx={cellHeadSx}>Subtotal</TableCell>
                      <TableCell align="right" sx={cellHeadSx}>Impuestos</TableCell>
                      <TableCell align="right" sx={cellHeadSx}>Total</TableCell>
                      <TableCell align="center" sx={cellHeadSx}>Estado</TableCell>
                      <TableCell align="center" className="no-print" sx={cellHeadSx}>
                        Acciones
                      </TableCell>
                    </TableRow>
                    {cotizaciones.map((cotizacion) => {
                      const esPendiente = cotizacion.estado !== 'IMPORTADA' && cotizacion.estado !== 'CERRADA';
                      const estadoLabel =
                        cotizacion.estado === 'IMPORTADA'
                          ? 'Importada en factura'
                          : cotizacion.estado === 'CERRADA'
                            ? 'Cerrada'
                            : 'Pendiente';
                      const estadoColor =
                        cotizacion.estado === 'IMPORTADA'
                          ? { bg: FP.color.successSoft, fg: FP.color.successDark }
                          : cotizacion.estado === 'CERRADA'
                            ? { bg: FP.color.warningSoft, fg: FP.color.warning }
                            : { bg: FP.color.accentBlueSoft, fg: FP.color.accentBlue };
                      return (
                        <TableRow key={cotizacion.id} hover>
                          <TableCell sx={{ fontWeight: 700, color: FP.color.textPrimary }}>
                            {cotizacion.numero || '—'}
                          </TableCell>
                          <TableCell
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={cotizacion.nombre || '—'}
                          >
                            {cotizacion.nombre || '—'}
                          </TableCell>
                          <TableCell
                            sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
                            title={cotizacion.cliente || '—'}
                          >
                            {cotizacion.cliente || '—'}
                          </TableCell>
                          <TableCell align="center">{formatearFecha(cotizacion.fecha)}</TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontVariantNumeric: 'tabular-nums', color: FP.color.textPrimary }}
                          >
                            ${(parseFloat(cotizacion.subtotal) || 0).toLocaleString('es-DO', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontVariantNumeric: 'tabular-nums', color: FP.color.textSecondary }}
                          >
                            ${(parseFloat(cotizacion.impuestos) || 0).toLocaleString('es-DO', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{
                              fontWeight: 700,
                              color: FP.color.textPrimary,
                              fontVariantNumeric: 'tabular-nums',
                            }}
                          >
                            ${(parseFloat(cotizacion.total) || 0).toLocaleString('es-DO', {
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            })}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              size="small"
                              label={estadoLabel}
                              sx={{
                                fontWeight: 700,
                                bgcolor: estadoColor.bg,
                                color: estadoColor.fg,
                                fontSize: '0.72rem',
                              }}
                            />
                          </TableCell>
                          <TableCell align="center" className="no-print">
                            {esPendiente && requiereRevisionMensaje(cotizacion) && (
                              <Chip
                                size="small"
                                label="Revisar mensaje"
                                onClick={() => handleAbrirComentario(cotizacion)}
                                sx={{
                                  mr: 0.5,
                                  fontWeight: 700,
                                  bgcolor: FP.color.warningSoft,
                                  color: FP.color.warning,
                                  cursor: 'pointer',
                                }}
                              />
                            )}
                            <Tooltip
                              title={
                                esPendiente
                                  ? 'Comentario / estado última conversación con cliente'
                                  : 'Solo en cotizaciones pendientes'
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleAbrirComentario(cotizacion)}
                                  disabled={!esPendiente}
                                  sx={{
                                    color: cotizacion.comentario_estado_cliente
                                      ? FP.color.info
                                      : FP.color.textSecondary,
                                    '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                    '&.Mui-disabled': { color: FP.color.textMuted },
                                  }}
                                >
                                  <CommentIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Reimprimir cotización">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleReimprimir(cotizacion)}
                                  disabled={cargandoReimprimir === cotizacion.id || !usuarioLog}
                                  sx={{
                                    color: FP.color.accentBlue,
                                    '&:hover': { bgcolor: FP.color.accentBlueSoft },
                                    '&.Mui-disabled': { color: FP.color.textMuted },
                                  }}
                                >
                                  {cargandoReimprimir === cotizacion.id ? (
                                    <CircularProgress size={18} sx={{ color: FP.color.accentBlue }} />
                                  ) : (
                                    <PrintIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Cerrar cotización">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleCerrar(cotizacion)}
                                  disabled={
                                    cargandoCerrar === cotizacion.id ||
                                    cotizacion.estado === 'CERRADA' ||
                                    !usuarioLog
                                  }
                                  sx={{
                                    color: FP.color.warning,
                                    '&:hover': { bgcolor: FP.color.warningSoft },
                                    '&.Mui-disabled': { color: FP.color.textMuted },
                                  }}
                                >
                                  {cargandoCerrar === cotizacion.id ? (
                                    <CircularProgress size={18} sx={{ color: FP.color.warning }} />
                                  ) : (
                                    <LockIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                            <Tooltip title="Eliminar cotización">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => handleEliminar(cotizacion)}
                                  disabled={
                                    !puedeEliminar ||
                                    cotizacion.estado === 'IMPORTADA' ||
                                    !usuarioLog
                                  }
                                  sx={{
                                    color: FP.color.danger,
                                    '&:hover': { bgcolor: FP.color.dangerSoft },
                                    '&.Mui-disabled': { color: FP.color.textMuted },
                                  }}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {cotizaciones.length > 0 && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  borderRadius: 2,
                  bgcolor: FP.color.surfaceAlt,
                  border: `1px solid ${FP.color.border}`,
                }}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Total cotizaciones
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1.25rem', fontVariantNumeric: 'tabular-nums' }}>
                      {cotizaciones.length}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Subtotal general
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1.25rem', fontVariantNumeric: 'tabular-nums' }}>
                      ${sumaSubtotal.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                      Total general
                    </Typography>
                    <Typography sx={{ fontWeight: 800, color: FP.color.navy, fontSize: '1.25rem', fontVariantNumeric: 'tabular-nums' }}>
                      ${sumaTotal.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>

      <VistaCotizacion
        open={vistaCotizacionAbierta}
        onClose={() => setVistaCotizacionAbierta(false)}
        cotizacionData={datosVistaCotizacion}
      />

      <Dialog
        open={comentarioDialogOpen}
        onClose={handleCerrarComentarioDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            boxShadow: FP.shadow.md,
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.6,
            borderBottom: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
          }}
        >
          <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
            Comentario – estado última conversación con cliente
          </Typography>
          {cotizacionComentario && (
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.82rem', mt: 0.5 }}>
              Cotización Nº {cotizacionComentario.numero} – {cotizacionComentario.cliente}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
          {cotizacionComentario && requiereRevisionMensaje(cotizacionComentario) && (
            <Alert
              severity="warning"
              sx={{
                mb: 2,
                borderRadius: 2,
                border: `1px solid ${FP.color.warning}`,
                bgcolor: FP.color.warningSoft,
              }}
            >
              Han pasado más de 5 días desde la última conversación. Revise el mensaje con el cliente.
            </Alert>
          )}
          <TextField
            fullWidth
            label="Fecha última conversación"
            type="date"
            value={fechaUltimaConversacion}
            InputLabelProps={{ shrink: true }}
            inputProps={{ readOnly: true }}
            sx={{ mt: 1, mb: 2, ...fpInputSx }}
          />
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={4}
            maxRows={10}
            label="Comentario"
            placeholder="Estado de la última conversación con el cliente..."
            value={comentarioTexto}
            onChange={(e) => setComentarioTexto(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={fpInputSx}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: { xs: 2, md: 3 },
            py: 1.5,
            borderTop: `1px solid ${FP.color.border}`,
            bgcolor: '#FAFBFE',
            gap: 1,
          }}
        >
          <Button
            onClick={handleCerrarComentarioDialog}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              color: FP.color.textSecondary,
              borderRadius: 1.4,
              '&:hover': { bgcolor: FP.color.surfaceAlt },
            }}
          >
            Cerrar
          </Button>
          <Button
            variant="contained"
            onClick={handleGuardarComentario}
            disabled={cargandoComentario || !usuarioLog}
            sx={{
              textTransform: 'none',
              borderRadius: 1.4,
              fontWeight: 700,
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              boxShadow: 'none',
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: FP.color.border, color: FP.color.textMuted },
            }}
          >
            {cargandoComentario ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ListadoCotizaciones;
